import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Snackbar } from "@mui/material";
import KeycloakContext from "KeycloakProvider";
import { FEATURE_REGISTRY, SIDEBAR_MENU } from "utils/accessRegistry";
import {
    useGetHomePagePreferencesQuery,
    useGetSidebarMenuPreferencesQuery,
    useUpsertHomePagePreferencesMutation,
    useUpsertSidebarMenuPreferencesMutation,
} from "api/homeApi";

// The backends return [] before the first save. Every save includes these
// sentinels, so an intentionally empty selection stays distinguishable.
export const SIDEBAR_SENTINEL_KEY = '__home__';
export const HOME_PAGE_SENTINEL_KEY = '__home_page__';

// Customizable sidebar keys are leaf entries: standalone items and children of groups.
// A group is shown whenever at least one of its children is visible.
export const getSidebarLeafKeys = (menu) => (Array.isArray(menu) ? menu : []).flatMap(item =>
    item.children && item.children.length > 0 ? item.children.map(child => child.key) : [item.key]
);

const ALL_SIDEBAR_KEYS = getSidebarLeafKeys(SIDEBAR_MENU);
const ALL_FEATURE_KEYS = FEATURE_REGISTRY.map(feature => feature.key);

export const filterMenuByVisibleKeys = (menu, visibleKeys) => {
    if (!Array.isArray(menu)) return [];
    if (!visibleKeys) return menu;
    return menu.reduce((acc, item) => {
        if (item.children && item.children.length > 0) {
            const children = item.children.filter(child => visibleKeys.has(child.key));
            if (children.length > 0) acc.push({ ...item, children });
        } else if (visibleKeys.has(item.key)) {
            acc.push(item);
        }
        return acc;
    }, []);
};

export const filterFeaturesByVisibleKeys = (features, visibleKeys) => {
    if (!Array.isArray(features)) return [];
    if (!visibleKeys) return features;
    return features.filter(feature => visibleKeys.has(feature.key));
};

const extractSavedKeys = (response, field) => {
    const keys = response?.[field] ?? response?.data?.[field];
    return Array.isArray(keys) && keys.length > 0 ? new Set(keys) : null;
};

// Keeps the previous choice for keys the user cannot see right now (e.g. KPI
// reports temporarily unavailable), so saving does not silently drop them.
const buildKeysToSave = (sentinel, availableKeys, draftKeys, allKeys, savedKeys) => {
    const availableSet = new Set(availableKeys);
    const preserved = allKeys.filter(key => !availableSet.has(key) && (!savedKeys || savedKeys.has(key)));
    return [sentinel, ...availableKeys.filter(key => draftKeys.has(key)), ...preserved];
};

const toggleKeysInSet = (set, keys, checked) => {
    const next = new Set(set);
    keys.forEach(key => (checked ? next.add(key) : next.delete(key)));
    return next;
};

const noop = () => {};
const DEFAULT_CONTEXT = {
    canCustomize: false,
    isEditing: false,
    isSaving: false,
    isLoading: false,
    hasLoadError: false,
    startEditing: noop,
    cancelEditing: noop,
    save: noop,
    retryLoad: noop,
    filterSidebarMenu: menu => menu,
    filterFeatures: features => features,
    sidebarDraft: new Set(),
    toggleSidebarKeys: noop,
    homeDraft: new Set(),
    toggleHomeKeys: noop,
};

const LayoutPreferencesContext = createContext(DEFAULT_CONTEXT);

export const useLayoutPreferences = () => useContext(LayoutPreferencesContext);

export function LayoutPreferencesProvider({ sidebarMenu, features, onStartEditing, onStopEditing, children }) {
    const { keycloak } = useContext(KeycloakContext) || {};
    const userIdentifier = keycloak?.tokenParsed?.sub || '';
    const queryOptions = { skip: !userIdentifier };

    const sidebarQuery = useGetSidebarMenuPreferencesQuery({ userIdentifier }, queryOptions);
    const homeQuery = useGetHomePagePreferencesQuery({ userIdentifier }, queryOptions);
    const [upsertSidebar] = useUpsertSidebarMenuPreferencesMutation();
    const [upsertHome] = useUpsertHomePagePreferencesMutation();

    const [savedSidebarKeys, setSavedSidebarKeys] = useState(null);
    const [savedHomeKeys, setSavedHomeKeys] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [sidebarDraft, setSidebarDraft] = useState(new Set());
    const [homeDraft, setHomeDraft] = useState(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);

    useEffect(() => {
        if (sidebarQuery.isFetching || isSaving) return;
        setSavedSidebarKeys(extractSavedKeys(sidebarQuery.currentData, 'menu_items'));
    }, [sidebarQuery.currentData, sidebarQuery.isFetching, isSaving]);

    useEffect(() => {
        if (homeQuery.isFetching || isSaving) return;
        setSavedHomeKeys(extractSavedKeys(homeQuery.currentData, 'home_items'));
    }, [homeQuery.currentData, homeQuery.isFetching, isSaving]);

    const availableSidebarKeys = useMemo(() => getSidebarLeafKeys(sidebarMenu), [sidebarMenu]);
    const availableFeatureKeys = useMemo(
        () => (Array.isArray(features) ? features.map(feature => feature.key) : []),
        [features]
    );

    const isLoading = Boolean(userIdentifier) && (sidebarQuery.isLoading || homeQuery.isLoading);
    const isFetching = sidebarQuery.isFetching || homeQuery.isFetching;
    const hasLoadError = sidebarQuery.isError || homeQuery.isError;
    const canCustomize = Boolean(userIdentifier);
    const { refetch: refetchSidebar } = sidebarQuery;
    const { refetch: refetchHome } = homeQuery;

    const startEditing = useCallback(() => {
        if (isLoading || isFetching || hasLoadError) return;
        setSaveError(false);
        setSidebarDraft(new Set(availableSidebarKeys.filter(key => !savedSidebarKeys || savedSidebarKeys.has(key))));
        setHomeDraft(new Set(availableFeatureKeys.filter(key => !savedHomeKeys || savedHomeKeys.has(key))));
        setIsEditing(true);
        if (onStartEditing) onStartEditing();
    }, [isLoading, isFetching, hasLoadError, availableSidebarKeys, availableFeatureKeys, savedSidebarKeys, savedHomeKeys, onStartEditing]);

    const cancelEditing = useCallback(() => {
        setIsEditing(false);
        setSaveError(false);
        if (onStopEditing) onStopEditing();
    }, [onStopEditing]);

    const save = useCallback(async () => {
        if (!userIdentifier || isSaving) return;
        const menuItems = buildKeysToSave(SIDEBAR_SENTINEL_KEY, availableSidebarKeys, sidebarDraft, ALL_SIDEBAR_KEYS, savedSidebarKeys);
        const homeItems = buildKeysToSave(HOME_PAGE_SENTINEL_KEY, availableFeatureKeys, homeDraft, ALL_FEATURE_KEYS, savedHomeKeys);

        setIsSaving(true);
        setSaveError(false);
        const [sidebarResult, homeResult] = await Promise.allSettled([
            upsertSidebar({ userIdentifier, menuItems }).unwrap(),
            upsertHome({ userIdentifier, homeItems }).unwrap(),
        ]);

        if (sidebarResult.status === 'fulfilled') setSavedSidebarKeys(new Set(menuItems));
        if (homeResult.status === 'fulfilled') setSavedHomeKeys(new Set(homeItems));

        const failures = [sidebarResult, homeResult].filter(result => result.status === 'rejected');
        if (failures.length > 0) {
            failures.forEach(result => console.error('Error saving layout preferences:', result.reason));
            setSaveError(true);
        } else {
            setIsEditing(false);
            if (onStopEditing) onStopEditing();
        }
        setIsSaving(false);
    }, [
        userIdentifier, isSaving, availableSidebarKeys, sidebarDraft, savedSidebarKeys,
        availableFeatureKeys, homeDraft, savedHomeKeys, upsertSidebar, upsertHome, onStopEditing,
    ]);

    const retryLoad = useCallback(() => {
        if (sidebarQuery.isError) refetchSidebar();
        if (homeQuery.isError) refetchHome();
    }, [sidebarQuery.isError, homeQuery.isError, refetchSidebar, refetchHome]);

    const filterSidebarMenu = useCallback(
        menu => (isLoading ? [] : filterMenuByVisibleKeys(menu, savedSidebarKeys)),
        [isLoading, savedSidebarKeys]
    );
    const filterFeatures = useCallback(
        list => (isLoading ? [] : filterFeaturesByVisibleKeys(list, savedHomeKeys)),
        [isLoading, savedHomeKeys]
    );

    const toggleSidebarKeys = useCallback((keys, checked) => {
        setSidebarDraft(prev => toggleKeysInSet(prev, keys, checked));
    }, []);
    const toggleHomeKeys = useCallback((keys, checked) => {
        setHomeDraft(prev => toggleKeysInSet(prev, keys, checked));
    }, []);

    const value = useMemo(() => ({
        canCustomize,
        isEditing,
        isSaving,
        isLoading: isLoading || isFetching,
        hasLoadError,
        startEditing,
        cancelEditing,
        save,
        retryLoad,
        filterSidebarMenu,
        filterFeatures,
        sidebarDraft,
        toggleSidebarKeys,
        homeDraft,
        toggleHomeKeys,
    }), [
        canCustomize, isEditing, isSaving, isLoading, isFetching, hasLoadError, startEditing, cancelEditing,
        save, retryLoad, filterSidebarMenu, filterFeatures, sidebarDraft, toggleSidebarKeys, homeDraft, toggleHomeKeys,
    ]);

    return (
        <LayoutPreferencesContext.Provider value={value}>
            {children}
            <Snackbar open={hasLoadError} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert
                    severity="error"
                    variant="filled"
                    action={<Button color="inherit" size="small" onClick={retryLoad}>Retry</Button>}
                >
                    Unable to load your layout preferences.
                </Alert>
            </Snackbar>
            <Snackbar
                open={saveError && !hasLoadError}
                autoHideDuration={6000}
                onClose={(_event, reason) => { if (reason !== 'clickaway') setSaveError(false); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="error" variant="filled" onClose={() => setSaveError(false)}>
                    Unable to save your layout preferences. Please try again.
                </Alert>
            </Snackbar>
        </LayoutPreferencesContext.Provider>
    );
}

export default LayoutPreferencesProvider;
